import { ThemeProvider, createTheme } from "@mui/material/styles";
import { createRoot } from "react-dom/client";
import ReactGA from "react-ga4";
import Root from "./Root";
import config from "./config";
import "./index.css";
import register from "./registerServiceWorker";
import configureStore from "./store";

const theme = createTheme({
    palette: {
        primary: {
            light: "#56addb",
            main: "#2C99D2",
            dark: "#1e6b93",
            contrastText: "#fff",
        },
        secondary: {
            light: "#68b36b",
            main: "#43A047",
            dark: "#2e7031",
            contrastText: "#fff",
        },
    },
    typography: {
        fontFamily: '"IBM Plex Sans", sans-serif',
    },
    components: {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: "0 1px 2px 0 rgba(34,54,70,.1)",
                },
                elevation2: {
                    boxShadow: "0 1px 2px 0 rgba(34,54,70,.1)",
                },
                elevation4: {
                    boxShadow: "none",
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: "white",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    marginLeft: "8px",
                    fontWeight: 600,
                    "&.Mui-focusVisible": {
                        outline: "2px solid rgba(44, 153, 210, 0.5)",
                    },
                },
                contained: {
                    boxShadow: "0 1px 2px rgba(34, 54, 70, .1)",
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    marginBottom: "20px",
                    marginTop: 0,
                    boxShadow: "0px 1px 2px rgba(34, 54, 70, 0.1)",
                    borderRadius: "3px",
                    "&.withTable": {
                        borderBottom: "1px solid #E0E0E0",
                        borderRadius: 0,
                        boxShadow: "none",
                        marginBottom: 0,
                    },
                    "&.Mui-expanded": {
                        marginBottom: "20px !important",
                        marginTop: "0 !important",
                        paddingBottom: "20px",
                        "&.withTable": {
                            marginBottom: "0 !important",
                        },
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: "#212121",
                    fontSize: "18px",
                    padding: "0 20px",
                    fontWeight: 700,
                    userSelect: "text",
                },
                content: {
                    paddingRight: "40px",
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    display: "block",
                    padding: "20px 20px 0",
                    "&.withTable": {
                        paddingTop: 0,
                    },
                },
            },
        },
        MuiModal: {
            styleOverrides: {
                root: {},
            },
        },
        MuiInput: {
            styleOverrides: {
                formControl: {
                    "label + &": {
                        marginTop: "22px",
                    },
                },
                root: {
                    ".MuiInputLabel-root + &": {
                        marginTop: "22px",
                    },
                    "&.Mui-focused": {
                        border: "1px solid rgba(44, 153, 210, 0.5)",
                    },
                    "&.Mui-disabled": {
                        cursor: "not-allowed",
                        border: "1px solid #E0E0E0",
                        "& input": {
                            cursor: "not-allowed",
                        },
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    padding: 0,
                    "&:not(.MuiSelect-root)": {
                        border: "1px solid #E0E0E0",
                        borderRadius: "4px",
                    },
                    "&.Mui-disabled": {
                        background: "#fafafa",
                        "&:hover": {
                            cursor: "not-allowed",
                        },
                    },
                },
                input: {
                    "&.Mui-disabled": {
                        WebkitTextFillColor: "rgba(0, 0, 0, 0.87) !important",
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                fullWidth: {
                    marginBottom: "30px",
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                shrink: {
                    transform: "none",
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    textTransform: "uppercase",
                    transform: "translate(0) scale(1)",
                    width: "100%",
                    fontSize: "13px",
                    fontWeight: 600,
                    borderBottom: "none",
                    "&.Mui-focused": {
                        color: "rgba(0, 0, 0, 0.54)",
                    },
                    "&.MuiInputLabel-root": {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: "0.875rem", // Change this to your desired font size
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    borderRadius: "4px",
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {},
        },
        MuiInputAdornment: {
            styleOverrides: {
                positionStart: {
                    marginRight: "4px",
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: "red",
                    "&.Mui-error": {
                        color: "red",
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {},
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: "4px 0px 4px 24px",
                    fontSize: "0.8125rem",
                    "&:last-child": {
                        paddingRight: "24px",
                    },
                },
                head: {
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "#9E9E9E",
                    fontSize: "12px",
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: "60px",
                },
                head: {
                    height: "36px",
                    fontSize: "12px",
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {},
                indicator: {
                    background: "#2C99D2",
                    height: "3px",
                },
                scroller: {},
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minWidth: "0 !important",
                    minHeight: 0,
                    paddingBottom: "15px",
                    marginRight: "30px",
                    "&.Mui-selected": {
                        color: "#000000",
                    },
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 600,
                    width: "auto",
                    padding: "10px 0 !important",
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: "#333",
                    "&:hover": {
                        cursor: "default",
                    },
                },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    color: "red",
                    background: "green",
                    fill: "red",
                },
            },
        },
        MuiCollapse: {
            styleOverrides: {
                root: {},
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    "& .MuiTableSortLabel-icon": {
                        height: 16,
                        marginRight: 4,
                        marginLeft: 4,
                    },
                },
                iconDirectionDesc: {
                    color: "red",
                    transform: "rotate(0deg)",
                },
                iconDirectionAsc: {
                    color: "green",
                    transform: "rotate(180deg)",
                },
            },
        },
        MuiList: {
            styleOverrides: {},
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: 14,
                    color: "#2C99D2",
                    fontWeight: 600,
                    paddingLeft: 16,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: "#2C99D2",
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "&.MuiSelect-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E0E0E0",
                            borderWidth: "1px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E0E0E0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(44, 153, 210, 0.5)",
                        },
                        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "red",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#fafafa",
                            color: "rgba(0, 0, 0, 0.38)",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E0E0E0",
                            },
                        },
                    },
                },
            },
        },
    },
});

const store = configureStore();
ReactGA.initialize(config.GA_ANALYTICS_TAG);

const root = createRoot(document.getElementById("root"));
root.render(
    <ThemeProvider theme={theme}>
        <Root store={store} />
    </ThemeProvider>
);

// Register cleanup service worker
register();
